import type { QueryKey } from '@tanstack/react-query';

/**
 * The SSE event name the API pushes every notification under
 * (`pushToUser` in apps/api/src/services/notification.ts); the payload is the
 * Notification row. A test holds the two sides to the same literal.
 */
export const NOTIFICATION_SSE_EVENT = 'notification';

/** How long a burst of notifications is coalesced before What needs me refetches. */
export const MY_WORK_SSE_DEBOUNCE_MS = 5_000;

/**
 * A `notification` SSE payload → what to refetch now, what to refetch after
 * a quiet spell, and whether to toast.
 *
 * Every notification also refreshes What needs me (`['me-work']`, 2026-10-18):
 * new work almost always arrives with one, so the list and the bell move
 * together. That refetch is `debounced` (5s trailing): a background job can
 * fan out dozens of notifications in a second, and each would otherwise run
 * the whole work read.
 */
export function notificationEventEffects(data: { type?: string; title?: string }): {
  invalidate: QueryKey[];
  debounced: QueryKey[];
  toast: string | null;
} {
  if (data.type === 'DRAFT_READY') {
    return {
      invalidate: [['notifications'], ['drafts']],
      debounced: [['me-work']],
      toast: data.title ?? 'New draft ready for review',
    };
  }
  return { invalidate: [['notifications']], debounced: [['me-work']], toast: null };
}

/**
 * A trailing debouncer for query invalidations: every key handed in within
 * `waitMs` of the last call is invalidated once, `waitMs` after the burst.
 */
export function createDebouncedInvalidator(
  invalidate: (key: QueryKey) => void,
  waitMs: number = MY_WORK_SSE_DEBOUNCE_MS,
): { add: (keys: QueryKey[]) => void; cancel: () => void } {
  const pending = new Map<string, QueryKey>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  return {
    add(keys) {
      if (keys.length === 0) return;
      for (const k of keys) pending.set(JSON.stringify(k), k);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        const keysNow = [...pending.values()];
        pending.clear();
        for (const k of keysNow) invalidate(k);
      }, waitMs);
    },
    cancel() {
      if (timer) clearTimeout(timer);
      timer = null;
      pending.clear();
    },
  };
}
